import React, { useState } from 'react';
import './PainExtractor.css';
import { useApi } from '../../hooks/useApi';
import { useAuth } from '../../contexts/AuthContext';
import useLocalStorage from '../../hooks/useLocalStorage';
import PainExtractorHero from './PainExtractorHero';
import HowTo from './HowTo';
import LegalNote from '../../components/LegalNote';
import ResultsList from './ResultsList';

function PainExtractor() {
  const [searchType, setSearchType] = useLocalStorage('painExtractor_searchType', 'subreddit');
  const [postUrl, setPostUrl] = useLocalStorage('painExtractor_postUrl', '');
  const [subredditUrl, setSubredditUrl] = useLocalStorage('painExtractor_subredditUrl', '');
  const [numberOfPosts, setNumberOfPosts] = useLocalStorage('painExtractor_numberOfPosts', 10);
  const [posts, setPosts] = useState([]);
  const { loading, error, request: fetchPostsApi, clearError } = useApi();
  const { fetchQuota } = useAuth();
  const [lastSearch, setLastSearch] = useState(null);

  // State for tracking which post's pains are being shown
  const [openPainExpanderPermalink, setOpenPainExpanderPermalink] = useState(null);
  // State for storing extracted pains for each post
  const [extractedPains, setExtractedPains] = useState({});
  const [extractingPains, setExtractingPains] = useState(new Set());
  const { request: extractPainsApi } = useApi();

  // Legal Consent State
  const [showConsentModal, setShowConsentModal] = useState(false);

  React.useEffect(() => {
    // Check if user has consented to the latest version (v1)
    const hasConsented = localStorage.getItem('painExtractorConsent_v1');
    if (!hasConsented) {
      setShowConsentModal(true);
    }
  }, []);

  const handleConsent = () => {
    localStorage.setItem('painExtractorConsent_v1', 'true');
    localStorage.setItem('painExtractorConsentDate', new Date().toISOString());
    setShowConsentModal(false);
  };

  const handleFocus = (event) => event.target.select();

  const handleSearch = async (e) => {
    e.preventDefault();
    setPosts([]);
    clearError();

    const currentSearch = {
      type: searchType,
      query: searchType === 'subreddit' ? subredditUrl : postUrl,
      numberOfPosts: searchType === 'subreddit' ? numberOfPosts : null
    };
    setLastSearch(currentSearch);

    let apiUrl = '';
    if (currentSearch.type === 'subreddit') {
      console.log('PainExtractor - Subreddit search:', currentSearch.query);
      if (!currentSearch.query) return;
      apiUrl = `/api/posts_for_pain_extraction?subreddit_url=${encodeURIComponent(currentSearch.query)}&number_of_posts=${currentSearch.numberOfPosts}`;
    } else {
      console.log('PainExtractor - Post search:', currentSearch.query);
      if (!currentSearch.query) return;
      apiUrl = `/api/post?post_url=${encodeURIComponent(currentSearch.query)}`;
    }

    try {
      const data = await fetchPostsApi(apiUrl);
      // The API for a single post might return an object, so we ensure it's an array
      setPosts(Array.isArray(data) ? data : [data]);
      fetchQuota();
    } catch (err) {
      console.error("Search failed:", err);
    }
  };

  const handleClearResults = () => {
    setPosts([]);
    clearError();
    setOpenPainExpanderPermalink(null);
    setExtractedPains({});
    setSubredditUrl('');
    setPostUrl('');
    setLastSearch(null);
    setNumberOfPosts(15);
  };

  const togglePainExpander = (permalink) => {
    if (openPainExpanderPermalink === permalink) {
      setOpenPainExpanderPermalink(null);
    } else {
      setOpenPainExpanderPermalink(permalink);
    }
  };

  const handleExtractPains = async (post) => {
    // If we already have pains for this post, just toggle the expander
    if (extractedPains[post.permalink]) {
      togglePainExpander(post.permalink);
      return;
    }

    setExtractingPains(prev => new Set(prev).add(post.permalink));

    try {
      const data = await extractPainsApi('/api/extract_pains', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ post: post })
      });

      // Update the extracted pains state
      setExtractedPains(prev => ({
        ...prev,
        [post.permalink]: data.pains
      }));

      // Open the expander
      togglePainExpander(post.permalink);
      fetchQuota();
    } catch (err) {
      console.error('Error extracting pains:', err);
    } finally {
      setExtractingPains(prev => {
        const newSet = new Set(prev);
        newSet.delete(post.permalink);
        return newSet;
      });
    }
  };

  const extractToJson = () => {
    if (posts.length === 0 || !lastSearch) return;

    // Augment posts with extracted pains
    const postsWithPains = posts.map(post => {
      const pains = extractedPains[post.permalink] || [];
      return {
        ...post,
        pains: pains
      };
    });

    const metadata = {
      search_type: lastSearch.type,
      result_count: postsWithPains.length,
      timestamp: new Date().toISOString()
    };

    if (lastSearch.type === 'subreddit') {
      metadata.subreddit_url = lastSearch.query;
      metadata.number_of_posts_searched = lastSearch.numberOfPosts;
    } else {
      metadata.post_url = lastSearch.query;
    }

    const data = {
      metadata: {
        ...metadata,
        disclaimer: "This data was extracted using WizeHelper's Pain Extractor tool. The content remains property of its respective authors on Reddit and is subject to Reddit's Terms of Service. Usage is restricted to research and analytical purposes.",
        terms_accepted_at: localStorage.getItem('painExtractorConsentDate') || new Date().toISOString(),
        api_compliance_notice: "This data was collected in compliance with Reddit's API Terms of Service and rate limits."
      },
      data: postsWithPains
    };

    const jsonString = JSON.stringify(data, null, 2);

    const blob = new Blob([jsonString], { type: 'application/json' });
    const url = URL.createObjectURL(blob);

    const a = document.createElement('a');
    a.href = url;
    a.download = `reddit_pains_${new Date().toISOString().slice(0, 19).replace(/:/g, '-')}.json`;
    document.body.appendChild(a);
    a.click();

    setTimeout(() => {
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    }, 0);
  };

  return (
    <>
      <title>Pain Extractor - WizeHelper</title>
      <meta name="description" content="Extract customer pain points from Reddit discussions to inform your product development and marketing." />
      <main className="mx-auto px-6 py-8" style={{ maxWidth: '88rem' }}>
        {showConsentModal && (
          <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4">
            <div className="bg-white dark:bg-gray-800 rounded-lg max-w-2xl w-full p-6 overflow-y-auto max-h-[90vh] shadow-2xl border border-gray-200 dark:border-gray-700">
              <LegalNote />

              <div className="mb-6 max-h-[60vh] overflow-y-auto pr-2 text-gray-700 dark:text-gray-300">
                <p className="mb-3">
                  This service analyzes publicly available content from Reddit. By proceeding, you acknowledge that:
                </p>
                <ul className="list-disc pl-5 space-y-2 mb-4">
                  <li>Your usage complies with Reddit's API Terms of Service and rate limits</li>
                  <li>AI-generated insights may contain errors or misinterpretations</li>
                  <li>Any business decisions based on this data are at your own risk</li>
                  <li>You will respect intellectual property and privacy rights of content creators</li>
                  <li>All data is processed in compliance with our privacy policy</li>
                </ul>
                <p className="text-sm border-t border-gray-200 dark:border-gray-700 pt-3 mt-3">
                  For detailed terms, please review our full
                  <a href="/terms-of-service" className="text-blue-600 dark:text-blue-400 hover:underline mx-1">Terms of Service</a>
                  and
                  <a href="/privacy-policy" className="text-blue-600 dark:text-blue-400 hover:underline mx-1">Privacy Policy</a>.
                </p>
              </div>

              <div className="flex justify-end pt-2">
                <button
                  onClick={handleConsent}
                  className="bg-[var(--color-primary-accent)] text-white font-semibold py-2.5 px-6 rounded-lg hover:bg-[var(--color-secondary-accent)] transition shadow-lg hover:shadow-xl active:scale-95 transform duration-100"
                >
                  I Agree and Accept
                </button>
              </div>
            </div>
          </div>
        )}
        <PainExtractorHero
          searchType={searchType}
          setSearchType={setSearchType}
          subredditUrl={subredditUrl}
          setSubredditUrl={setSubredditUrl}
          numberOfPosts={numberOfPosts}
          setNumberOfPosts={setNumberOfPosts}
          postUrl={postUrl}
          setPostUrl={setPostUrl}
          handleSearch={handleSearch}
          loading={loading}
          handleFocus={handleFocus}
        />
        <LegalNote />

        {error && (
          <div className="p-4 mb-4 text-sm text-red-700 bg-red-100 rounded-lg" role="alert">
            {error.status === 403 || error.status === 429 ? (
              error.message
            ) : (
              <><span className="font-medium">Error!</span> {error.message}</>
            )}
          </div>
        )}

        {loading ? (
          <div className="text-center my-12">
            <div className="spinner"></div>
          </div>
        ) : (
          <>
            {posts.length > 0 ? (
              <ResultsList
                posts={posts}
                extractToJson={extractToJson}
                handleClearResults={handleClearResults}
                handleExtractPains={handleExtractPains}
                extractingPains={extractingPains}
                extractedPains={extractedPains}
                openPainExpanderPermalink={openPainExpanderPermalink}
                togglePainExpander={togglePainExpander}
              />
            ) : lastSearch ? (
              <div className="text-center my-12 bg-gray-50 dark:bg-gray-800/50 rounded-lg p-8" data-testid="painExtractor-noPostsFound">
                <h3 className="text-2xl font-bold text-gray-800 dark:text-white">No Posts Found</h3>
                <p className="mt-2 text-gray-600 dark:text-gray-400">
                  We couldn't find any posts matching your criteria. Try adjusting your search terms.
                </p>
              </div>
            ) : (
              <div className="my-12">
                <HowTo searchType={searchType} />
              </div>
            )}
          </>
        )}
      </main>
    </>
  );
}

export default PainExtractor;
